import React, { useState, useRef, useEffect, useCallback } from 'react';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { COLOR } from '@src/styles/color';
import apis from '@src/apis';
import { ERROR_CODE } from '@src/errors/code';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { LANDING_PAGE_URL, VBEE_ZALO_URL } from '@src/configs';
import Typography from '@src/new-components/Typography';
import { useSelector } from 'react-redux';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { VOICE_STATUS_VERIFY_SPEAKER } from '@src/constants/voiceCloning';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getS3Url } from '@src/services/voiceCloning';
import useFakeProgress from '@src/hooks/useFakeProgress';
import CommonButton from '@src/new-components/Button';
import { convertAudioToWav } from '@src/services/audio';
import Sentences from './Sentences';
import AudioRecorderStatus from './AudioRecorderStatus';
import RecordAgain from './RecordAgain';
import { StyledRecord, StyledRecordVerify } from './index.style';
import ProcessingAudio from './ProcessingAudio';

const MAX_DURATION = 15;
const TIME_COUNT_DOWN = 3;
const ERRORS = {
  PERMISSION_DENIED: 'PermissionDeniedError',
  NOT_ALLOWED: 'NotAllowedError',
  NOT_FOUND: 'NotFoundError',
};
const FAKE_PROGRESS_INTERVAL = 100;
const MAX_FAKE_PROGRESS_TIME = 30000;

const VoiceRecord = ({
  termsAccepted,
  professionalSentence,
  refetchProfessionalSentence,
  voiceId,
  onSuccessVerifyVoice,
}) => {
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);

  const { t } = useTranslation();
  const { progress, start, stop, reset } = useFakeProgress({
    interval: FAKE_PROGRESS_INTERVAL,
    maxProgress: 99,
    maxTime: MAX_FAKE_PROGRESS_TIME,
  });
  const [metadataAudio, setMetadataAudio] = useState(null);
  const [loadingVerifyVoice, setLoadingVerifyVoice] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isCountdown, setIsCountdown] = useState(false);
  const [countdown, setCountdown] = useState(TIME_COUNT_DOWN);
  const [showStartText, setShowStartText] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [isMicAllowed, setIsMicAllowed] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [voiceCheckStatus, setVoiceCheckStatus] = useState(null);
  const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [voice, setVoice] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const streamRef = useRef(null);
  const lastRecordedBlobRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const micPermissionPromiseRef = useRef(null);

  const fetchVoiceById = async () => {
    const response = await apis.voiceCloning.getVoiceCloningVoice(voiceId);
    return response;
  };

  const improveAudioUploadEnabled = getFeatureValue(
    FEATURE_KEYS.IMPROVE_AUDIO_UPLOAD,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  useEffect(() => {
    (async () => {
      const response = await fetchVoiceById();
      setVoice(response?.result?.voice);
    })();
  }, [voiceId]);

  const clearStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
      streamRef.current = null;
    }
  };

  const clearTimers = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const checkDeviceHasMic = useCallback(async () => {
    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.some((device) => device.kind === 'audioinput');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      return err.name !== ERRORS.NOT_FOUND;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isMicAllowed === null && navigator?.permissions) {
      navigator.permissions
        .query({ name: 'microphone' })
        .then(async (result) => {
          if (cancelled) return;
          const hasMic = await checkDeviceHasMic();
          setIsMicAllowed(result.state === 'granted' && hasMic);
        })
        .catch(() => setIsMicAllowed(false));
    }
    return () => {
      cancelled = true;
    };
  }, [isMicAllowed, checkDeviceHasMic]);

  useEffect(() => () => reset(), [reset]);

  useEffect(
    () => () => {
      clearStream();
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
      clearTimers();
    },
    [],
  );

  const handleRequestMic = useCallback(async () => {
    setIsProcessing(true);
    if (micPermissionPromiseRef.current) {
      await micPermissionPromiseRef.current;
      setIsProcessing(false);
      return;
    }
    micPermissionPromiseRef.current = navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        setIsMicAllowed(true);
        stream.getTracks().forEach((track) => track.stop());
        setIsMicPermissionDenied(false);
        setAudioError(null);
        setVoiceCheckStatus(null);
        setIsProcessing(false);
        setIsCountdown(true);
        setCountdown(TIME_COUNT_DOWN);
        setShowStartText(false);
      })
      .catch((err) => {
        if (
          [
            ERRORS.NOT_ALLOWED,
            ERRORS.PERMISSION_DENIED,
            ERRORS.NOT_FOUND,
          ].includes(err.name)
        ) {
          setIsMicPermissionDenied(true);
          setAudioError(t('micPermissionDenied'));
        }
        setIsMicAllowed(false);
        setIsProcessing(false);
      })
      .finally(() => {
        micPermissionPromiseRef.current = null;
      });
    await micPermissionPromiseRef.current;
  }, [t]);

  const handleStart = useCallback(() => {
    setAudioError(null);
    setVoiceCheckStatus(null);
    if (isMicAllowed) {
      setIsCountdown(true);
      setCountdown(TIME_COUNT_DOWN);
      setShowStartText(false);
    } else {
      handleRequestMic();
    }
  }, [isMicAllowed, handleRequestMic]);

  const handleStartRecording = useCallback(async () => {
    setIsRecording(true);
    setRecordTime(0);
    audioChunks.current = [];
    setMetadataAudio(null);
    setAudioError(null);
    setVoiceCheckStatus(null);
    lastRecordedBlobRef.current = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new window.MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/wav' });
        lastRecordedBlobRef.current = blob;
        clearStream();
      };

      recorder.start();
    } catch {
      setIsRecording(false);
    }
  }, []);

  const handleStopRecording = useCallback(async () => {
    setIsLoading(true);
    setAudioError(null);
    setVoiceCheckStatus(null);
    start();
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }
    setTimeout(async () => {
      try {
        const blob = lastRecordedBlobRef.current;
        if (!blob) {
          setIsLoading(false);
          setAudioError(t('errorOccurred'));
          return;
        }

        // Convert audio to WAV format with specified parameters
        const wavBlob = await convertAudioToWav(blob);
        const url = URL.createObjectURL(wavBlob);
        lastRecordedBlobRef.current = null;
        setIsRecording(false);

        const s3Url = await getS3Url({
          voiceId,
          labelText: professionalSentence?.originalText,
          fileUrl: url,
        });
        setAudioError(null);
        setVoiceCheckStatus(null);
        setIsMicPermissionDenied(false);

        if (s3Url) {
          stop();
          await new Promise((resolve) => setTimeout(resolve, 500));
          setIsLoading(false);
          setMetadataAudio({
            audioLink: s3Url,
            recordTime: recordTime || MAX_DURATION,
            fileName: `${moment().format('YYYY-MM-DD HH:mm:ss')}.wav`,
          });
        }
      } catch (error) {
        stop();
        await new Promise((resolve) => setTimeout(resolve, 500));
        setIsLoading(false);
        setAudioError(t('errorOccurred'));
      }
    }, 100);
  }, [recordTime, start, stop, t]);

  useEffect(() => {
    if (isCountdown && countdown > 0) {
      countdownTimerRef.current = setTimeout(
        () => setCountdown((c) => c - 1),
        1000,
      );
    }
    if (isCountdown && countdown === 0) {
      setShowStartText(true);
      countdownTimerRef.current = setTimeout(() => {
        setIsCountdown(false);
        setShowStartText(false);
        handleStartRecording();
      }, 1000);
    }
    return () => {
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [isCountdown, countdown, handleStartRecording]);

  const handleReset = useCallback(() => {
    refetchProfessionalSentence();
    setMetadataAudio(null);
    setRecordTime(0);
    setIsRecording(false);
    setIsCountdown(false);
    setCountdown(TIME_COUNT_DOWN);
    setShowStartText(false);
    setAudioError(null);
    setVoiceCheckStatus(null);
    setIsMicPermissionDenied(false);
    audioChunks.current = [];
    mediaRecorderRef.current = null;
    lastRecordedBlobRef.current = null;
    clearStream();
    clearTimers();
  }, []);

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordTime((prev) => {
          if (prev >= MAX_DURATION) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
            setTimeout(handleStopRecording, 0);
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    };
  }, [isRecording, handleStopRecording]);

  const verifyYourVoice = useCallback(async () => {
    setLoadingVerifyVoice(true);
    try {
      const data = await apis.voiceCloning.verificationVoice(voiceId, {
        labelText: professionalSentence?.originalText,
        fileUrl: metadataAudio?.audioLink,
      });

      if (data?.result) {
        let pollingInterval = null;
        const pollFetchVoice = async () => {
          const response = await fetchVoiceById();
          setVoice(response?.result?.voice);
          const verifySpeakerStatus =
            response?.result?.voice?.verifySpeakerStatus;
          const remainingAttempts =
            response?.result?.voice?.verificationAttempts?.remaining || 0;

          if (verifySpeakerStatus) {
            if (verifySpeakerStatus === VOICE_STATUS_VERIFY_SPEAKER.SUCCESS) {
              setLoadingVerifyVoice(false);
              onSuccessVerifyVoice();
            }
            if (verifySpeakerStatus === VOICE_STATUS_VERIFY_SPEAKER.FAILED) {
              setLoadingVerifyVoice(false);
              setAudioError(
                t('errorVoiceNotMatchUpload', { remainingAttempts }),
              );
            }
            // polling done
            if (pollingInterval) {
              clearInterval(pollingInterval);
              pollingInterval = null;
            }
          }
        };
        // Start polling every 2s
        pollingInterval = setInterval(pollFetchVoice, 3000);
      } else {
        const response = await fetchVoiceById();
        const remainingAttempts =
          response?.result?.voice?.verificationAttempts?.remaining || 0;
        setVoice(response?.result?.voice);
        setLoadingVerifyVoice(false);
        if (data?.errorCode === ERROR_CODE.VOICE_ALREADY_VERIFIED) {
          setVoiceCheckStatus(ERROR_CODE.VOICE_ALREADY_VERIFIED);
          setAudioError(t('verificationFailedBecauseVoiceStatusChanged'));
          return;
        }
        setAudioError(t('voiceMismatch', { remainingAttempts }));
      }
    } catch (error) {
      setLoadingVerifyVoice(false);
      setAudioError(t('errorOccurred'));
    }
  }, [recordTime, t, voiceId, professionalSentence, metadataAudio?.audioLink]);

  if (isLoading || metadataAudio?.audioLink) {
    return (
      <ProcessingAudio
        metadataAudio={metadataAudio}
        title={t('uploadingAudio')}
        progress={progress}
        audioError={audioError}
        loadingVerifyVoice={loadingVerifyVoice}
        verifyYourVoice={verifyYourVoice}
        handleReset={handleReset}
        voiceCheckStatus={voiceCheckStatus}
      />
    );
  }

  const showRecorderStatus = isCountdown || showStartText || isRecording;
  const showStartButton =
    !isRecording &&
    !isCountdown &&
    !showStartText &&
    !metadataAudio?.audioLink &&
    !audioError;

  const showErrorMaxAttempts = () => {
    const attempts = voice?.verificationAttempts;
    const lastTime = attempts?.lastTime ? moment(attempts.lastTime) : null;

    if (attempts?.remaining === 0 && lastTime) {
      const unlockTime = lastTime.clone().add(24, 'hours');
      return moment().isBefore(unlockTime);
    }

    return false;
  };
  const content = `Hỗ trợ kiểm tra xác minh giọng nói cho nhân bản giọng chuyên nghiệp bằng cách tải tệp ghi âm:
- Tên giọng: ${voice?.name}
- ID giọng: ${voice?.id}
  `;
  const supportMetadata = {
    userId: user.id,
    content,
    titleSupport: 'Hỗ trợ về xác minh giọng cho nhân bản giọng',
  };
  const base64TicketMetadata = btoa(
    encodeURIComponent(JSON.stringify(supportMetadata)),
  );
  const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

  const linkSupport = `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.VOICE_CLONING}`;

  return (
    <StyledRecordVerify>
      <StyledRecord showStartButton={showStartButton}>
        <Sentences
          professionalSentence={professionalSentence}
          isCountdown={isCountdown}
          isRecording={isRecording}
          showErrorMaxAttempts={showErrorMaxAttempts()}
          lastTime={voice?.verificationAttempts?.lastTime}
          improveAudioUploadEnabled={improveAudioUploadEnabled}
        />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {showRecorderStatus && (
            <AudioRecorderStatus
              handleStopRecording={handleStopRecording}
              recordTime={recordTime}
              isRecording={isRecording}
              isCountdown={isCountdown}
              countdown={showStartText ? t('start') : countdown}
              handleReset={handleReset}
            />
          )}
          {audioError && (
            <RecordAgain
              isMicPermissionDenied={isMicPermissionDenied}
              handleRequestMic={handleRequestMic}
              audioError={audioError}
              verifyYourVoice={verifyYourVoice}
              loadingVerifyVoice={loadingVerifyVoice}
            />
          )}

          {showStartButton &&
            (improveAudioUploadEnabled && showErrorMaxAttempts() ? (
              <Typography
                variant="body-14-regular"
                color={COLOR.descriptionUpload}
                dangerouslySetInnerHTML={{
                  __html: t('contactCS', {
                    zaloUrl: VBEE_ZALO_URL,
                    linkSupport,
                  }),
                }}
              />
            ) : (
              voice && (
                <CommonButton
                  variant="contained"
                  textProps={{
                    variantText: 'body-14-bold',
                    color: COLOR.darkBlue,
                  }}
                  customStyle={{
                    width: 'max-content',
                    margin: '0 auto',
                    height: '48px',
                    padding: '12px 40px',
                  }}
                  onClick={handleStart}
                  disabled={
                    isProcessing || !termsAccepted || showErrorMaxAttempts()
                  }
                >
                  {t('startRecordingV2')}
                </CommonButton>
              )
            ))}
        </div>
      </StyledRecord>
    </StyledRecordVerify>
  );
};

export default VoiceRecord;
